/**
 * Victorian metered taxi fare estimation.
 * Rates as published for Melbourne metropolitan taxis.
 */

export type RateBand = "day" | "overnight" | "peak";

export const RATES: Record<
  RateBand,
  { label: string; flagfall: number; perKm: number; perMin: number }
> = {
  day: { label: "Day rate (9am–5pm)", flagfall: 5.4, perKm: 2.1, perMin: 0.7 },
  overnight: { label: "Overnight rate (5pm–9am)", flagfall: 6.75, perKm: 2.3, perMin: 0.8 },
  peak: { label: "Peak rate", flagfall: 8.0, perKm: 2.55, perMin: 0.85 },
};

/** Speed threshold (km/h) below which the meter charges by time instead of distance. */
export const SPEED_THRESHOLD_KMH = 21;

export const VEHICLE_MULTIPLIERS: Record<string, number> = {
  "Premium Sedan": 1,
  "Premium SUV": 1.25,
  Maxi: 1.45,
};

function isPeakHoliday(d: Date): boolean {
  const m = d.getMonth() + 1;
  const day = d.getDate();
  const hour = d.getHours();
  // Christmas Day, Boxing Day, New Year's Day — all day
  if (m === 12 && (day === 25 || day === 26)) return true;
  if (m === 1 && day === 1) return true;
  // New Year's Eve from 6pm
  if (m === 12 && day === 31 && hour >= 18) return true;
  return false;
}

export function getRateBand(d: Date): RateBand {
  const hour = d.getHours();
  const dow = d.getDay(); // 0 Sun … 5 Fri, 6 Sat

  if (isPeakHoliday(d)) return "peak";

  // 10pm–4am Friday and Saturday nights.
  // 10pm–midnight on Fri/Sat, or midnight–4am on Sat/Sun (the same nights).
  const lateFriSat = hour >= 22 && (dow === 5 || dow === 6);
  const earlySatSun = hour < 4 && (dow === 6 || dow === 0);
  if (lateFriSat || earlySatSun) return "peak";

  // Day rate 9am–5pm, otherwise overnight.
  if (hour >= 9 && hour < 17) return "day";
  return "overnight";
}

export type FareEstimate = {
  band: RateBand;
  label: string;
  distanceKm: number;
  durationMin: number;
  total: number;
  low: number;
  high: number;
};

export function estimateFare({
  distanceKm,
  durationMin,
  when,
  vehicle,
  returnTrip = false,
}: {
  distanceKm: number;
  durationMin: number;
  when: Date;
  vehicle: string;
  returnTrip?: boolean;
}): FareEstimate {
  const band = getRateBand(when);
  const rate = RATES[band];

  // Minutes spent moving slower than the threshold are charged by time.
  const fastMinutes = (distanceKm / SPEED_THRESHOLD_KMH) * 60;
  const slowMinutes = Math.max(0, durationMin - fastMinutes);

  let total = rate.flagfall + distanceKm * rate.perKm + slowMinutes * rate.perMin;
  total *= VEHICLE_MULTIPLIERS[vehicle] ?? 1;
  if (returnTrip) total *= 2;

  return {
    band,
    label: rate.label,
    distanceKm,
    durationMin,
    total: round(total),
    low: round(total * 0.9),
    high: round(total * 1.15),
  };
}

function round(n: number): number {
  return Math.round(n * 100) / 100;
}

/** Tolerant parser for the free-text date/time fields in the booking form. */
export function parseWhen(dateStr: string, timeStr: string): Date {
  const combined = `${dateStr.trim()} ${timeStr.trim()}`.trim();
  const direct = new Date(combined);
  if (!Number.isNaN(direct.getTime())) return direct;
  const dateOnly = new Date(dateStr.trim());
  if (!Number.isNaN(dateOnly.getTime())) return dateOnly;
  return new Date();
}

export function formatAud(n: number): string {
  return `$${n.toFixed(2)}`;
}
